'use strict'
import { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography
} from '@material-ui/core'

// Global profitability settings editor. Seeds from the mapped settings the drawer
// already holds (target is a RATIO; this field DISPLAYS a percent), PATCHes the
// singleton, then calls onSaved (the hook's refresh) so open panels recompute.
export default function ProfitabilitySettingsModal ({ open, onClose, url, settings, onSaved }) {
  const [burden, setBurden] = useState('32')
  const [targetPct, setTargetPct] = useState('50')
  const [mileage, setMileage] = useState('0.655')
  const [factor, setFactor] = useState('1')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [validationError, setValidationError] = useState('')

  // Seed the fields from settings whenever the modal opens (or settings change).
  // onEnter fires the same seed for the MUI transition, but the useEffect makes it
  // reliable under jsdom (where the transition may not run).
  const seed = () => {
    setBurden(String(settings?.burdenRate ?? 32))
    setTargetPct(String(Math.round((settings?.target ?? 0.5) * 100)))
    setMileage(String(settings?.mileageRate ?? 0.655))
    setFactor(String(settings?.driveRoundTripFactor ?? 1))
    setSaveError('')
    setValidationError('')
  }
  // Re-seed fields whenever the modal opens or the underlying settings change.
  // `seed` is an intentionally-fresh closure each render; deps are open + settings.
  useEffect(() => {
    if (open) seed()
  }, [open, settings])

  const handleSave = () => {
    // No base URL → can't PATCH (would hit "undefined/api/..."). Defensive: every
    // current consumer passes a url, but don't fire a broken request if one slips.
    if (url == null) { setSaveError('Cannot save: no API URL.'); return }
    const burdenRate = Number(burden)
    const pct = Number(targetPct)
    const mileageRate = Number(mileage)
    const driveRoundTripFactor = Number(factor)

    // Client-side guard mirrors the server rules for instant feedback.
    if (!(burdenRate > 0)) { setValidationError('Burden rate must be greater than 0.'); return }
    if (!(pct > 0 && pct <= 100)) { setValidationError('Target margin must be between 0 and 100%.'); return }
    if (!(mileageRate >= 0)) { setValidationError('Mileage rate must be 0 or greater.'); return }
    if (!(driveRoundTripFactor >= 0)) { setValidationError('Drive round-trip factor must be 0 or greater.'); return }

    const body = {
      burdenRate: Number(burden),
      targetMargin: Number(targetPct) / 100,
      mileageRate: Number(mileage),
      driveRoundTripFactor: Number(factor)
    }

    setValidationError('')
    setSaveError('')
    setSaving(true)
    axios.patch(`${url}/api/profitability-settings`, body)
      .then(() => {
        if (onSaved) onSaved()
        setSaving(false)
        onClose()
      })
      .catch((err) => {
        setSaving(false)
        setSaveError(err?.response?.data?.error || 'Failed to save')
      })
  }

  const errorText = validationError || saveError

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth onEnter={seed}>
      <DialogTitle style={{ borderBottom: '1px solid var(--df-line)', paddingBottom: 12, fontWeight: 700 }}>
        Profitability Settings
        <Typography variant="caption" component="div" style={{ color: 'var(--df-ink-3)', fontWeight: 400, marginTop: 2 }}>
          Applies to all properties (global)
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 16 }}>
          <TextField
            label="Burden rate ($/man-hr)"
            variant="outlined"
            size="small"
            type="number"
            fullWidth
            value={burden}
            onChange={(e) => setBurden(e.target.value)}
          />
          <TextField
            label="Target margin (%)"
            variant="outlined"
            size="small"
            type="number"
            fullWidth
            value={targetPct}
            onChange={(e) => setTargetPct(e.target.value)}
            helperText="% (e.g. 50 = 50%)"
          />
          <TextField
            label="Mileage rate ($/mi)"
            variant="outlined"
            size="small"
            type="number"
            fullWidth
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
          />
          <TextField
            label="Drive round-trip factor"
            variant="outlined"
            size="small"
            type="number"
            fullWidth
            value={factor}
            onChange={(e) => setFactor(e.target.value)}
            helperText="1 = one-way leg per visit"
          />
          {errorText && (
            <Typography variant="caption" style={{ color: 'var(--df-accent)' }}>
              {errorText}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid var(--df-line)', padding: '12px 24px' }}>
        <Button onClick={onClose} style={{ color: 'var(--df-ink-2)' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || url == null}
          style={{ background: 'var(--df-primary)', color: '#fff' }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
